import { Injectable } from '@angular/core';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import {
  AffiliateMallMetadata,
  CampaignPointsMetadata,
  CardLinkedOfferMetadata,
  CardSpendMetadata,
  FlightSegment,
  GiftCardMetadata,
  PointsTransaction,
  PointsTransactionCategory,
  PointsTransferMetadata,
  ProductCategory,
  PurchaseEraserMetadata,
  RealtimeCardSpendMetadata,
  RedeemCashMetadata,
  TravelCarMetadata,
  TravelFlightMetadata,
  TravelHotelMetadata,
} from '@rd/types';
import { StringHelper } from './string.helper';

// Values and Keys are base on TE
const FLIGHT_CABIN = {
  Y: 'Economy',
  S: 'Premium Economy',
  C: 'Business',
  F: 'First Class',
};

// Transaction category type
type ProductCategoryDetails =
  | 'earn'
  | 'transfer'
  | 'travelHotel'
  | 'travelCar'
  | 'travelFlight'
  | 'redeemGiftCard'
  | 'manualAdjustment'
  | 'cardSpend'
  | 'cardLinkedOffer'
  | 'normal'
  | 'giftCard'
  | 'redeemCash'
  | 'cardSpendReversal'
  | 'welcomeMiles'
  | 'refund'
  | 'writeOff'
  | 'expiry'
  | 'genericCampaignPoints'
  | 'affiliateMall'
  | 'purchaseEraser'
  | 'sharePointsAccrual'
  | 'sharePointsRedemption'
  | 'realtimeCardSpend';

// Transaction Refund category subType
type RefundProductSubType =
  | 'gift_card'
  | 'cashback'
  | 'points_transfer'
  | 'cars'
  | 'flights'
  | 'hotels'
  | 'default';

@Injectable({
  providedIn: 'root',
})
export class PointsTransactionHelper {
  constructor(
    protected numberService: NumberService,
    protected currencyService: CurrencyService,
    protected stringHelper: StringHelper,
  ) {}

  // We truncate this just so the customer has a reference ID when contacting us, but they don't
  // have the entire ID which is long
  public displayedId(pointsTransaction: PointsTransaction): string {
    return pointsTransaction.id.substring(0, 8).toUpperCase();
  }

  public displayedOrderId(pointsTransaction: PointsTransaction): string {
    if (!pointsTransaction.orderId) {
      return '';
    }

    return pointsTransaction.orderId.substring(0, 8).toUpperCase();
  }

  public formattedAmount(pointsTransaction: PointsTransaction): string {
    const CATEGORIES_WITH_POSITIVE_AMOUNT = [
      PointsTransactionCategory.Accrual,
      PointsTransactionCategory.Refund,
      PointsTransactionCategory.WriteOffNegative,
    ];
    const { category, amount } = pointsTransaction;

    if (amount === 0) {
      return this.numberService.formatNumber(0);
    }

    if (CATEGORIES_WITH_POSITIVE_AMOUNT.includes(category)) {
      return `+${this.numberService.formatNumber(amount)}`;
    } else {
      return `-${this.numberService.formatNumber(amount)}`;
    }
  }

  public formattedBalance(pointsTransaction: PointsTransaction): string {
    // TODO: TS-strict-mode revisit what should we return here
    return pointsTransaction.balance
      ? this.numberService.formatNumber(pointsTransaction.balance)
      : '';
  }

  public longDateFormat(): string {
    return 'dd/MM/yyyy';
  }

  isFullyLoaded(pointsTransaction: PointsTransaction | undefined): boolean {
    return !!(pointsTransaction && pointsTransaction.id);
  }

  public categoryTranslationKey(
    category: PointsTransactionCategory | 'all',
  ): string {
    // There is no need to differentiate between write_off and write_off_negative
    // on the frontend. From a consumer's point of view, they are the same category
    if (category === PointsTransactionCategory.WriteOffNegative) {
      return 'app.write_off';
    }

    return `app.${category}`;
  }

  isCardLinkedOffer(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.CardLinkedOffer;
  }

  isTravelBooking(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.TravelBooking;
  }

  isTravelHotelBooking(pointsTransaction: PointsTransaction): boolean {
    return (
      this.isTravelBooking(pointsTransaction) &&
      (pointsTransaction.orderItemData as TravelHotelMetadata)
        .productSubtype === 'hotels'
    );
  }

  isTravelCarBooking(pointsTransaction: PointsTransaction): boolean {
    return (
      this.isTravelBooking(pointsTransaction) &&
      (pointsTransaction.orderItemData as TravelCarMetadata).productSubtype ===
        'cars'
    );
  }

  isTravelFlightBooking(pointsTransaction: PointsTransaction): boolean {
    return (
      this.isTravelBooking(pointsTransaction) &&
      (pointsTransaction.orderItemData as TravelFlightMetadata)
        .productSubtype === 'flights'
    );
  }

  giftCardMetadataAmountAndName(pointsTransaction: PointsTransaction): string {
    const { amount, productName } =
      pointsTransaction.orderItemData as GiftCardMetadata;

    return `${productName} ${this.currencyService.formatCurrency(
      amount,
      'symbol',
    )}`;
  }

  giftCardMetadataCashAmount(
    pointsTransaction: PointsTransaction,
  ): string | null {
    const { cashAmount } = pointsTransaction.orderItemData as GiftCardMetadata;

    return cashAmount && cashAmount > 0
      ? this.currencyService.formatCurrency(cashAmount / 100, 'codeWithSpace')
      : null;
  }

  giftCardMetadataRecipientName(
    pointsTransaction: PointsTransaction,
  ): string | undefined {
    const { recipient } = pointsTransaction.orderItemData as GiftCardMetadata;

    if (recipient) {
      const { firstName, lastName } = recipient;

      return `${firstName} ${lastName}`;
    }
  }

  pointsTransferMetadataMemberName(
    pointsTransaction: PointsTransaction,
  ): string {
    const { membershipInformation } =
      pointsTransaction.orderItemData as PointsTransferMetadata;

    return `${membershipInformation.firstName} ${membershipInformation.lastName}`;
  }

  redeemCashProductCategory(pointsTransaction: PointsTransaction): string {
    const orderItemData = pointsTransaction.orderItemData as RedeemCashMetadata;
    // NN return in a format like Credit.Card
    // We only take the Credit part for display purpose
    // Default for other tenant will still be entryType
    const productCategory =
      orderItemData.membership?.enrollment?.productCategory ||
      pointsTransaction.entryType;

    return productCategory?.toLowerCase().includes('credit')
      ? 'credit'
      : 'debit';
  }

  redeemCashMetadataAmount(pointsTransaction: PointsTransaction): string {
    const { amount, currency } =
      pointsTransaction.orderItemData as RedeemCashMetadata;

    return `${currency} ${amount}`;
  }

  purchaseEraserMetadataAmount(pointsTransaction: PointsTransaction): string {
    const { amount } =
      pointsTransaction.orderItemData as PurchaseEraserMetadata;
    return `${this.currencyService.formatCurrency(amount, 'symbol')}`;
  }

  cardSpendMetadataTotalSpend(pointsTransaction: PointsTransaction): string {
    const { spendAmount, currency } =
      pointsTransaction.metadata as CardSpendMetadata;

    if (spendAmount >= 0) {
      return `${currency} ${this.numberService.formatNumber(spendAmount)}`;
    } else {
      return `(${currency} ${this.numberService.formatNumber(
        Math.abs(spendAmount),
      )})`;
    }
  }

  hotelMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid, currencyCode } =
      pointsTransaction.orderItemData as TravelHotelMetadata;

    // We can't use currency service as that (the currency the customer is using) could be different from the
    // currency the customer used when booking the hotel
    // This logic is also repeated with cars and flights
    return `${currencyCode} ${this.numberService.formatNumber(
      cashPaid,
      '1.0-0',
    )}`;
  }

  hotelMetadataGuestName(pointsTransaction: PointsTransaction): string {
    const { firstName, lastName } =
      pointsTransaction.orderItemData as TravelHotelMetadata;

    return `${firstName} ${lastName}`;
  }

  carMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid, currencyCode } =
      pointsTransaction.orderItemData as TravelCarMetadata;

    return `${currencyCode} ${this.numberService.formatNumber(
      cashPaid,
      '1.0-0',
    )}`;
  }

  carMetadataDriverName(pointsTransaction: PointsTransaction): string {
    const { firstName, lastName } =
      pointsTransaction.orderItemData as TravelCarMetadata;

    return `${firstName} ${lastName}`;
  }

  flightMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid, currencyCode } =
      pointsTransaction.orderItemData as TravelFlightMetadata;

    return `${currencyCode} ${this.numberService.formatNumber(
      cashPaid,
      '1.0-0',
    )}`;
  }

  flightMetadataFormatPassenger(
    pointsTransaction: PointsTransaction,
  ): string[] {
    const { passengers } =
      pointsTransaction.orderItemData as TravelFlightMetadata;
    const passengersName: string[] = [];

    passengers.forEach((passenger) => {
      passengersName.push(`${passenger.firstName} ${passenger.lastName}`);
    });

    return passengersName;
  }

  formatFlightSegment(flightSegment: FlightSegment): string {
    const { fromAirport, toAirport } = flightSegment;
    return `${fromAirport} - ${toAirport}`;
  }

  transferMetadataAmount(pointsTransaction: PointsTransaction): string {
    const { transferAmount, loyaltyCurrencyName } =
      pointsTransaction.orderItemData as PointsTransferMetadata;
    return `${this.numberService.formatNumber(
      transferAmount,
    )} ${loyaltyCurrencyName}`;
  }

  flightMetadataFormatCabinClass(
    pointsTransaction: PointsTransaction,
  ): string | undefined {
    const { cabinClass } =
      pointsTransaction.orderItemData as TravelFlightMetadata;
    // @ts-ignore
    return FLIGHT_CABIN[cabinClass];
  }

  // Card Linked Offer, Affiliate Mall use metadata instead of orderItemData, as they do not have an order attached to them.

  cardLinkedOfferMetadataProductName(
    pointsTransaction: PointsTransaction,
  ): string {
    const { productName } =
      pointsTransaction.metadata as CardLinkedOfferMetadata;
    return productName;
  }

  cardLinkedOfferMetadataLast4Digits(
    pointsTransaction: PointsTransaction,
  ): string | null {
    const { last4 } = pointsTransaction.metadata as CardLinkedOfferMetadata;
    return last4;
  }

  affiliateMallMetadataProductName(
    pointsTransaction: PointsTransaction,
  ): string {
    const { productName } = pointsTransaction.metadata as AffiliateMallMetadata;
    return productName;
  }

  cashAmountWithCurrency(pointsTransaction: PointsTransaction): string {
    const { cashAmount } = pointsTransaction.metadata as
      | CardLinkedOfferMetadata
      | AffiliateMallMetadata;
    return `${this.currencyService.formatCurrency(cashAmount, 'symbol')}`;
  }

  realtimeCardSpendAmountWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { amountSpent } =
      pointsTransaction.metadata as RealtimeCardSpendMetadata;
    return `${this.currencyService.formatCurrency(amountSpent, 'symbol')}`;
  }

  transactionProductCategoryType(
    pointsTransaction: PointsTransaction,
  ): ProductCategoryDetails {
    if (this.isSharePointsAccrual(pointsTransaction)) {
      return 'sharePointsAccrual';
    }
    if (this.isSharePointsRedemption(pointsTransaction)) {
      return 'sharePointsRedemption';
    }
    if (this.isCardLinkedOffer(pointsTransaction)) {
      return 'cardLinkedOffer';
    }
    if (this.isGenericCampaignPoints(pointsTransaction)) {
      return 'genericCampaignPoints';
    }
    if (this.isTransfer(pointsTransaction)) {
      return 'transfer';
    }
    if (this.isTravelHotelBooking(pointsTransaction)) {
      return 'travelHotel';
    }
    if (this.isTravelCarBooking(pointsTransaction)) {
      return 'travelCar';
    }
    if (this.isTravelFlightBooking(pointsTransaction)) {
      return 'travelFlight';
    }
    if (this.isManualAdjustment(pointsTransaction)) {
      return 'manualAdjustment';
    }
    if (this.isGiftCard(pointsTransaction)) {
      return 'giftCard';
    }
    if (this.isRedeemCash(pointsTransaction)) {
      return 'redeemCash';
    }
    if (this.isWelcomeMiles(pointsTransaction)) {
      return 'welcomeMiles';
    }
    if (this.isAffiliateMall(pointsTransaction)) {
      return 'affiliateMall';
    }
    if (this.isPurchaseEraser(pointsTransaction)) {
      return 'purchaseEraser';
    }
    if (this.isRealTimeCardSpend(pointsTransaction)) {
      return 'realtimeCardSpend';
    }
    if (this.isEarn(pointsTransaction)) {
      return 'earn';
    }
    if (this.isCardSpendReversal(pointsTransaction)) {
      return 'cardSpendReversal';
    }
    if (this.isRefund(pointsTransaction)) {
      return 'refund';
    }
    if (this.isWriteOff(pointsTransaction)) {
      return 'writeOff';
    }
    if (this.isExpiry(pointsTransaction)) {
      return 'expiry';
    }

    return 'normal';
  }

  transactionRefundProductSubType(
    pointsTransaction: PointsTransaction,
  ): RefundProductSubType {
    if (
      'loyaltyProgramId' in pointsTransaction.metadata &&
      'transferAmount' in pointsTransaction.metadata
    ) {
      return 'points_transfer';
    }

    if ('productSubtype' in pointsTransaction.metadata) {
      return pointsTransaction.metadata.productSubtype;
    }

    if ('cardId' in pointsTransaction.metadata) {
      return 'gift_card';
    }

    if (
      'loyaltyProgramId' in pointsTransaction.metadata &&
      'amountInCents' in pointsTransaction.metadata
    ) {
      return 'cashback';
    }

    return 'default';
  }

  private isTransfer(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.PointsTransfer;
  }

  private isManualAdjustment(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.ManualAdjustment;
  }

  private isGiftCard(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.GiftCard;
  }

  private isRedeemCash(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.RedeemCash;
  }

  private isAffiliateMall(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.AffiliateMall;
  }

  private isPurchaseEraser(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.productType === ProductCategory.PurchaseEraser;
  }

  private isRealTimeCardSpend(pointsTransaction: PointsTransaction): boolean {
    return (
      pointsTransaction.productType === ProductCategory.RealtimeCardSpend &&
      (pointsTransaction.category === PointsTransactionCategory.Accrual ||
        pointsTransaction.category === PointsTransactionCategory.Reversal)
    );
  }

  private isSharePointsRedemption(
    pointsTransaction: PointsTransaction,
  ): boolean {
    return (
      pointsTransaction.productType === ProductCategory.PointsShare &&
      pointsTransaction.category === PointsTransactionCategory.Redemption
    );
  }

  private isSharePointsAccrual(pointsTransaction: PointsTransaction): boolean {
    return (
      pointsTransaction.productType === ProductCategory.PointsShare &&
      pointsTransaction.category === PointsTransactionCategory.Accrual
    );
  }

  private isRefund(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.category === PointsTransactionCategory.Refund;
  }

  private isWelcomeMiles(pointsTransaction: PointsTransaction): boolean {
    const campaignPointsMetadata =
      pointsTransaction.metadata as CampaignPointsMetadata;
    return (
      pointsTransaction.productType === ProductCategory.CampaignPoints &&
      !!campaignPointsMetadata.campaignId &&
      campaignPointsMetadata.campaignId.includes('welcome_miles')
    );
  }

  private isGenericCampaignPoints(
    pointsTransaction: PointsTransaction,
  ): boolean {
    const campaignPointsMetadata =
      pointsTransaction.metadata as CampaignPointsMetadata;
    return (
      pointsTransaction.productType === ProductCategory.CampaignPoints &&
      !!campaignPointsMetadata.campaignId &&
      !campaignPointsMetadata.campaignId.includes('welcome_miles')
    );
  }

  private isEarn(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.category === PointsTransactionCategory.Accrual;
  }

  private isCardSpendReversal(pointsTransaction: PointsTransaction): boolean {
    return (
      pointsTransaction.category === PointsTransactionCategory.Reversal &&
      pointsTransaction.productType === ProductCategory.CardSpend
    );
  }

  private isWriteOff(pointsTransaction: PointsTransaction): boolean {
    return (
      pointsTransaction.category === PointsTransactionCategory.WriteOff ||
      pointsTransaction.category === PointsTransactionCategory.WriteOffNegative
    );
  }

  private isExpiry(pointsTransaction: PointsTransaction): boolean {
    return pointsTransaction.category === PointsTransactionCategory.Expiry;
  }
}
